import { useState, useMemo, useEffect } from 'react';
import { Search, Users, RefreshCw, ArrowRight, ArrowLeft, ArrowLeftRight, RotateCcw, UserPlus } from 'lucide-react';
import PlayerCard from './PlayerCard';
import PlayerEditModal from './PlayerEditModal';
import PhotoLightbox from './PhotoLightbox';
import Pagination from './Pagination';
import type { Player, Team, AppUser, GenderFilter, SideFilter, PlayerDetails } from '../types';

interface PlayersViewProps {
  players: Player[];
  teams: Team[];
  currentUser: AppUser;
  searchQuery: string;
  setSearchQuery: (val: string) => void;
  onManagePlayer: (player: Player) => void;
  onRefresh: () => void;
  loading?: boolean;
  onCreatePlayer?: (data: { name: string; mobile_number: string; details: Partial<PlayerDetails> }) => Promise<boolean>;
  onUpdatePlayer?: (playerId: string, updates: { name?: string; details?: Partial<PlayerDetails> }) => Promise<boolean>;
  onDeletePlayer?: (playerId: string) => Promise<boolean>;
}

interface FilterCardProps {
  icon: React.ReactNode;
  label: string;
  count: number;
  totalCount?: number;
  active: boolean;
  onClick: () => void;
  color?: 'indigo' | 'amber' | 'pink' | 'blue' | 'green' | 'purple';
}

const FilterCard = ({ icon, label, count, totalCount, active, onClick, color = 'indigo' }: FilterCardProps) => {
  const colorStyles = {
    indigo: 'bg-indigo-100 text-indigo-600',
    amber: 'bg-amber-100 text-amber-600',
    pink: 'bg-pink-100 text-pink-600',
    blue: 'bg-blue-100 text-blue-600',
    green: 'bg-green-100 text-green-600',
    purple: 'bg-purple-100 text-purple-600',
  };

  return (
    <button
      onClick={onClick}
      className={`flex items-center gap-3 p-4 rounded-xl border-2 transition-all ${
        active
          ? 'border-indigo-500 bg-white shadow-md'
          : 'border-slate-200 bg-white hover:border-slate-300'
      }`}
    >
      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${colorStyles[color]}`}>
        {icon}
      </div>
      <div className="text-left">
        <p className="text-xs text-slate-500 font-medium">{label}</p>
        <p className="text-2xl font-bold text-slate-800">
          {count}
          {totalCount !== undefined && totalCount !== count && (
            <span className="text-sm text-slate-400 font-normal ml-1">/ {totalCount}</span>
          )}
        </p>
      </div>
    </button>
  );
};

const PlayersView = ({
  players,
  teams,
  currentUser,
  searchQuery,
  setSearchQuery,
  onManagePlayer,
  onRefresh,
  loading = false,
  onCreatePlayer,
  onUpdatePlayer,
  onDeletePlayer
}: PlayersViewProps) => {
  const [genderFilter, setGenderFilter] = useState<GenderFilter>('all');
  const [sideFilter, setSideFilter] = useState<SideFilter>('all');

  // Modal states
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editingPlayer, setEditingPlayer] = useState<Player | null>(null);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxPlayer, setLightboxPlayer] = useState<Player | null>(null);

  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(18);

  // Players are already sorted by created_datetime (newest first) in useFirestoreData

  // Calculate total counts (for reference)
  const totalCounts = useMemo(() => {
    const male = players.filter(p => p.details?.player_gender?.toLowerCase() === 'male').length;
    const female = players.filter(p => p.details?.player_gender?.toLowerCase() === 'female').length;
    const right = players.filter(p => p.details?.playing_side?.toLowerCase() === 'right').length;
    const left = players.filter(p => p.details?.playing_side?.toLowerCase() === 'left').length;
    const both = players.filter(p => p.details?.playing_side?.toLowerCase() === 'both').length;
    return { total: players.length, male, female, right, left, both };
  }, [players]);

  // Calculate dynamic counts based on current opposite filter
  // Gender counts should update when side filter changes, and vice versa
  const dynamicCounts = useMemo(() => {
    // Filter by side for gender counts
    const sideFiltered = sideFilter === 'all'
      ? players
      : players.filter(p => p.details?.playing_side?.toLowerCase() === sideFilter);

    // Filter by gender for side counts
    const genderFiltered = genderFilter === 'all'
      ? players
      : players.filter(p => p.details?.player_gender?.toLowerCase() === genderFilter);

    return {
      total: players.length,
      // Gender counts respect side filter
      male: sideFiltered.filter(p => p.details?.player_gender?.toLowerCase() === 'male').length,
      female: sideFiltered.filter(p => p.details?.player_gender?.toLowerCase() === 'female').length,
      // Side counts respect gender filter
      right: genderFiltered.filter(p => p.details?.playing_side?.toLowerCase() === 'right').length,
      left: genderFiltered.filter(p => p.details?.playing_side?.toLowerCase() === 'left').length,
      both: genderFiltered.filter(p => p.details?.playing_side?.toLowerCase() === 'both').length,
    };
  }, [players, genderFilter, sideFilter]);

  // Filter players (already sorted by created_datetime from hook)
  const filteredPlayers = useMemo(() => {
    return players.filter(p => {
      // Gender filter
      if (genderFilter !== 'all') {
        const gender = p.details?.player_gender?.toLowerCase();
        if (gender !== genderFilter) return false;
      }
      // Side filter
      if (sideFilter !== 'all') {
        const side = p.details?.playing_side?.toLowerCase();
        if (side !== sideFilter) return false;
      }
      // Search filter
      if (searchQuery.trim()) {
        const q = searchQuery.toLowerCase();
        return p.name.toLowerCase().includes(q) ||
          p.mobile_number.includes(q) ||
          p.details?.player_full_name?.toLowerCase().includes(q) ||
          p.details?.jersey_name?.toLowerCase().includes(q) ||
          p.details?.jersey_number?.includes(q);
      }
      return true;
    });
  }, [players, genderFilter, sideFilter, searchQuery]);

  // Paginate filtered players
  const paginatedPlayers = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    return filteredPlayers.slice(startIndex, startIndex + itemsPerPage);
  }, [filteredPlayers, currentPage, itemsPerPage]);

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [genderFilter, sideFilter, searchQuery]);

  // Reset all filters
  const resetFilters = () => {
    setGenderFilter('all');
    setSideFilter('all');
    setSearchQuery('');
  };

  const isFiltersActive = genderFilter === 'all' && sideFilter === 'all';

  // Handlers for player CRUD
  const handleEditPlayer = (player: Player) => {
    setEditingPlayer(player);
    setEditModalOpen(true);
  };

  const handleCreatePlayer = () => {
    setEditingPlayer(null);  // null means create mode
    setEditModalOpen(true);
  };

  const handleSavePlayer = async (data: { name: string; mobile_number: string; details: Partial<PlayerDetails> }) => {
    if (editingPlayer) {
      // Update existing player
      if (onUpdatePlayer) {
        return await onUpdatePlayer(editingPlayer.id, { name: data.name, details: data.details });
      }
    } else {
      // Create new player
      if (onCreatePlayer) {
        return await onCreatePlayer(data);
      }
    }
    return false;
  };

  const handleDeletePlayer = async (playerId: string) => {
    if (onDeletePlayer) {
      return await onDeletePlayer(playerId);
    }
    return false;
  };

  const handlePhotoClick = (player: Player) => {
    if (player.details?.photo_url) {
      setLightboxPlayer(player);
      setLightboxOpen(true);
    }
  };

  return (
    <div className="space-y-6">
      {/* Active Filters Summary */}
      {(!isFiltersActive || searchQuery) && (
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-sm text-slate-500">Active filters:</span>
          {genderFilter !== 'all' && (
            <span className="px-2 py-1 bg-amber-100 text-amber-700 text-sm rounded-full">
              {genderFilter === 'male' ? 'Male' : 'Female'}
            </span>
          )}
          {sideFilter !== 'all' && (
            <span className="px-2 py-1 bg-blue-100 text-blue-700 text-sm rounded-full capitalize">
              {sideFilter} Side
            </span>
          )}
          {searchQuery && (
            <span className="px-2 py-1 bg-slate-100 text-slate-700 text-sm rounded-full">
              "{searchQuery}"
            </span>
          )}
          <span className="text-sm font-medium text-indigo-600 ml-2">
            Showing {filteredPlayers.length} of {totalCounts.total} players
          </span>
          <button
            onClick={resetFilters}
            className="ml-auto flex items-center gap-1 px-3 py-1 text-sm text-slate-600 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors"
          >
            <RotateCcw className="w-4 h-4" /> Reset All
          </button>
        </div>
      )}

      {/* Filter Cards - Row 1: Gender */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <FilterCard
          icon={<Users className="w-5 h-5" />}
          label="All Players"
          count={filteredPlayers.length}
          totalCount={totalCounts.total}
          active={isFiltersActive}
          onClick={resetFilters}
          color="indigo"
        />
        <FilterCard
          icon={<Users className="w-5 h-5" />}
          label="Male Players"
          count={dynamicCounts.male}
          totalCount={totalCounts.male}
          active={genderFilter === 'male'}
          onClick={() => setGenderFilter(genderFilter === 'male' ? 'all' : 'male')}
          color="amber"
        />
        <FilterCard
          icon={<Users className="w-5 h-5" />}
          label="Female Players"
          count={dynamicCounts.female}
          totalCount={totalCounts.female}
          active={genderFilter === 'female'}
          onClick={() => setGenderFilter(genderFilter === 'female' ? 'all' : 'female')}
          color="pink"
        />
      </div>

      {/* Filter Cards - Row 2: Playing Side */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <FilterCard
          icon={<ArrowRight className="w-5 h-5" />}
          label="Right Side"
          count={dynamicCounts.right}
          totalCount={totalCounts.right}
          active={sideFilter === 'right'}
          onClick={() => setSideFilter(sideFilter === 'right' ? 'all' : 'right')}
          color="blue"
        />
        <FilterCard
          icon={<ArrowLeft className="w-5 h-5" />}
          label="Left Side"
          count={dynamicCounts.left}
          totalCount={totalCounts.left}
          active={sideFilter === 'left'}
          onClick={() => setSideFilter(sideFilter === 'left' ? 'all' : 'left')}
          color="green"
        />
        <FilterCard
          icon={<ArrowLeftRight className="w-5 h-5" />}
          label="Both Sides"
          count={dynamicCounts.both}
          totalCount={totalCounts.both}
          active={sideFilter === 'both'}
          onClick={() => setSideFilter(sideFilter === 'both' ? 'all' : 'both')}
          color="purple"
        />
      </div>

      {/* Search Bar & Refresh */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1 max-w-lg">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
          <input
            type="text"
            placeholder="Search by name, mobile, or jersey..."
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-3 rounded-xl border border-slate-300 focus:ring-2 focus:ring-indigo-500 focus:outline-none bg-white"
          />
        </div>
        <button
          onClick={onRefresh}
          disabled={loading}
          className="flex items-center justify-center gap-2 px-6 py-3 bg-indigo-600 text-white font-semibold rounded-xl hover:bg-indigo-700 transition-colors disabled:opacity-50"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          Refresh List
        </button>

        {/* Add Player Button (Admin only) */}
        {currentUser.role === 'admin' && onCreatePlayer && (
          <button
            onClick={handleCreatePlayer}
            className="flex items-center justify-center gap-2 px-6 py-3 bg-green-600 text-white font-semibold rounded-xl hover:bg-green-700 transition-colors"
          >
            <UserPlus className="w-4 h-4" />
            Add Player
          </button>
        )}
      </div>

      {/* Players Grid */}
      {filteredPlayers.length === 0 ? (
        <div className="text-center py-16">
          <Users className="w-12 h-12 text-slate-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-slate-600">No Players Found</h3>
          <p className="text-slate-400">Try adjusting your search or filters.</p>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {paginatedPlayers.map(player => (
              <PlayerCard
                key={player.id}
                player={player}
                teams={teams}
                currentUser={currentUser}
                onManagePlayer={onManagePlayer}
                onEditPlayer={currentUser.role === 'admin' && onUpdatePlayer ? handleEditPlayer : undefined}
                onPhotoClick={handlePhotoClick}
              />
            ))}
          </div>

          {/* Pagination */}
          <div className="bg-white rounded-xl border border-slate-200 p-4">
            <Pagination
              currentPage={currentPage}
              totalItems={filteredPlayers.length}
              itemsPerPage={itemsPerPage}
              onPageChange={setCurrentPage}
              onItemsPerPageChange={setItemsPerPage}
            />
          </div>
        </>
      )}

      {/* Player Edit Modal */}
      <PlayerEditModal
        player={editingPlayer}
        isOpen={editModalOpen}
        onClose={() => {
          setEditModalOpen(false);
          setEditingPlayer(null);
        }}
        onSave={handleSavePlayer}
        onDelete={onDeletePlayer ? handleDeletePlayer : undefined}
      />

      {/* Photo Lightbox */}
      <PhotoLightbox
        imageUrl={lightboxPlayer?.details?.photo_url || ''}
        playerName={lightboxPlayer?.name || ''}
        isOpen={lightboxOpen}
        onClose={() => {
          setLightboxOpen(false);
          setLightboxPlayer(null);
        }}
      />
    </div>
  );
};

export default PlayersView;

